import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ChevronDown,
  ChevronRight,
  Download,
  Eye,
  FileCode2,
  History,
  Search,
  Trash2,
  Upload,
  X,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/contexts/ToastContext'
import { formatFileSize } from '@/lib/evidenceFileTypes'
import { NmsConfiguration, projectsApi } from '@/lib/projectsApi'
import { canDownload, canUpload, canManage } from '@/lib/roles'

interface NmsConfigurationListProps {
  projectId?: string
  refreshToken?: number
  showProject?: boolean
  onChanged?: () => void
}

function vesselLabel(row: NmsConfiguration): string {
  return [
    row.shipyard_name,
    row.hull_number,
    row.vessel_name || row.project_name,
  ].filter(Boolean).join(' / ')
}

export const NmsConfigurationList: React.FC<NmsConfigurationListProps> = ({
  projectId,
  refreshToken = 0,
  showProject = false,
  onChanged,
}) => {
  const { user } = useAuth()
  const { success, error: showError } = useToast()
  const inputRef = useRef<HTMLInputElement>(null)
  const [rows, setRows] = useState<NmsConfiguration[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null)
  const [preview, setPreview] = useState<{ row: NmsConfiguration; text: string } | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [history, setHistory] = useState<Record<string, NmsConfiguration[]>>({})
  const [historyLoading, setHistoryLoading] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<NmsConfiguration | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setRows(await projectsApi.listNmsConfigurations(projectId))
    } catch (err: any) {
      showError(err.message ?? 'Failed to load NMS configurations')
    } finally {
      setLoading(false)
    }
  }, [projectId, showError])

  useEffect(() => { void load() }, [load, refreshToken])

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return rows
    return rows.filter(row => [
      row.original_filename,
      row.device_name,
      row.ip_address,
      row.config_label,
      row.relative_path,
      row.hull_number,
      row.vessel_name,
      row.project_name,
      row.shipyard_name,
    ].filter(Boolean).join(' ').toLowerCase().includes(term))
  }, [rows, search])

  const groups = useMemo(() => {
    if (!showProject) return [['', filtered] as const]
    const grouped = new Map<string, NmsConfiguration[]>()
    for (const row of filtered) {
      const key = row.project_id
      const list = grouped.get(key)
      if (list) list.push(row)
      else grouped.set(key, [row])
    }
    return [...grouped.entries()]
  }, [filtered, showProject])

  const handleUpload = async (files: FileList | null) => {
    if (!projectId || !files?.length) return
    const selected = [...files].filter(file => /\.ini$/i.test(file.name))
    if (selected.length !== files.length) showError('Only .ini files can be uploaded')
    if (!selected.length) return
    setUploadProgress({ done: 0, total: selected.length })
    let created = 0
    let duplicates = 0
    try {
      for (let i = 0; i < selected.length; i += 1) {
        const result = await projectsApi.uploadNmsConfiguration(projectId, selected[i])
        if (result.duplicate) duplicates += 1
        else created += 1
        setUploadProgress({ done: i + 1, total: selected.length })
      }
      success(`Saved ${created} NMS configuration${created === 1 ? '' : 's'}${duplicates ? `; ${duplicates} unchanged duplicate${duplicates === 1 ? '' : 's'} skipped` : ''}`)
      await load()
      onChanged?.()
    } catch (err: any) {
      showError(err.message ?? 'NMS upload failed')
      await load()
    } finally {
      setUploadProgress(null)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  const openPreview = async (row: NmsConfiguration) => {
    setPreviewLoading(true)
    try {
      const blob = await projectsApi.fetchNmsContent(row.id)
      setPreview({ row, text: await blob.text() })
    } catch (err: any) {
      showError(err.message ?? 'Preview failed')
    } finally {
      setPreviewLoading(false)
    }
  }

  const toggleHistory = async (row: NmsConfiguration) => {
    if (history[row.id]) {
      setHistory(prev => {
        const next = { ...prev }
        delete next[row.id]
        return next
      })
      return
    }
    setHistoryLoading(row.id)
    try {
      const revisions = await projectsApi.listNmsHistory(row.id)
      setHistory(prev => ({ ...prev, [row.id]: revisions }))
    } catch (err: any) {
      showError(err.message ?? 'Failed to load revision history')
    } finally {
      setHistoryLoading(null)
    }
  }

  const download = async (row: NmsConfiguration) => {
    try {
      await projectsApi.downloadNmsConfiguration(row)
    } catch (err: any) {
      showError(err.message ?? 'Download failed')
    }
  }

  const remove = async (row: NmsConfiguration) => {
    try {
      await projectsApi.deleteNmsConfiguration(row.id)
      success(`Deleted ${row.original_filename} revision ${row.revision_number}`)
      setHistory({})
      await load()
      onChanged?.()
    } catch (err: any) {
      showError(err.message ?? 'Delete failed')
    }
  }

  const rowActions = (row: NmsConfiguration, compact = false) => (
    <div className="flex items-center justify-end gap-1">
      {canDownload(user) && (
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void openPreview(row)}
            disabled={previewLoading}
            title="Preview configuration"
          >
            <Eye className="h-4 w-4" />
            {!compact && <span className="hidden xl:inline">Preview</span>}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => void download(row)} title="Download configuration">
            <Download className="h-4 w-4" />
          </Button>
        </>
      )}
      {canManage(user) && (
        <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(row)} title="Delete revision">
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      )}
    </div>
  )

  const renderRows = (groupRows: NmsConfiguration[]) => (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[860px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:bg-slate-900/40">
            <th className="px-3 py-2.5 font-medium">Configuration</th>
            <th className="px-3 py-2.5 font-medium">Device</th>
            <th className="px-3 py-2.5 font-medium">IP address</th>
            <th className="px-3 py-2.5 font-medium">Revision</th>
            <th className="px-3 py-2.5 font-medium">Uploaded</th>
            <th className="px-3 py-2.5 text-right font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {groupRows.map(row => (
            <React.Fragment key={row.id}>
              <tr className="border-b border-slate-100 align-top hover:bg-slate-50/70 dark:border-slate-800 dark:hover:bg-slate-800/50">
                <td className="max-w-[360px] px-3 py-3">
                  <div className="flex items-start gap-2">
                    <FileCode2 className="mt-0.5 h-4 w-4 shrink-0 text-blue-700" />
                    <div className="min-w-0">
                      <div className="truncate font-medium text-slate-900 dark:text-white" title={row.original_filename}>
                        {row.original_filename}
                      </div>
                      <div className="mt-0.5 truncate text-xs text-slate-400" title={row.relative_path ?? undefined}>
                        {formatFileSize(row.file_size_bytes)}
                        {row.config_label ? ` · ${row.config_label}` : ''}
                        {row.relative_path && row.relative_path !== row.original_filename ? ` · ${row.relative_path}` : ''}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3 text-slate-600 dark:text-slate-300">{row.device_name || '—'}</td>
                <td className="px-3 py-3 font-mono text-xs text-slate-600 dark:text-slate-300">{row.ip_address || '—'}</td>
                <td className="px-3 py-3">
                  <button
                    type="button"
                    onClick={() => void toggleHistory(row)}
                    className="inline-flex items-center gap-1 rounded px-1.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-blue-900/30"
                    title="Show revision history"
                  >
                    {history[row.id] ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                    v{row.revision_number}
                    {historyLoading === row.id && <span className="animate-pulse">…</span>}
                  </button>
                </td>
                <td className="px-3 py-3 text-xs text-slate-500">
                  <div>{new Date(row.created_at).toLocaleString()}</div>
                  {row.uploader_email && <div className="mt-0.5 truncate">{row.uploader_email}</div>}
                </td>
                <td className="px-3 py-2">{rowActions(row)}</td>
              </tr>
              {history[row.id] && (
                <tr className="border-b border-slate-100 bg-slate-50/60 dark:border-slate-800 dark:bg-slate-900/30">
                  <td colSpan={6} className="px-5 py-3">
                    <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <History className="h-3.5 w-3.5" /> Revision history
                    </div>
                    <div className="space-y-1.5">
                      {history[row.id].map(revision => (
                        <div key={revision.id} className="flex items-center gap-3 rounded border border-slate-200 bg-white px-3 py-2 text-xs dark:border-slate-700 dark:bg-slate-800">
                          <span className="w-10 font-semibold">v{revision.revision_number}</span>
                          <span className="min-w-0 flex-1 truncate text-slate-500">
                            {new Date(revision.created_at).toLocaleString()} · {formatFileSize(revision.file_size_bytes)} · {revision.content_sha256.slice(0, 12)}
                          </span>
                          {revision.is_current && <span className="rounded bg-green-100 px-1.5 py-0.5 text-green-700">Current</span>}
                          {rowActions(revision, true)}
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  )

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder="Search filename, device, IP address, or vessel"
            className="h-9 w-full rounded-md border border-slate-300 bg-white pl-9 pr-9 text-sm outline-none focus:border-blue-700 focus:ring-2 focus:ring-blue-700/15 dark:border-slate-600 dark:bg-slate-900"
          />
          {search && (
            <button type="button" onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {projectId && canUpload(user) && (
          <>
            <input
              ref={inputRef}
              type="file"
              accept=".ini"
              multiple
              className="hidden"
              onChange={event => void handleUpload(event.target.files)}
            />
            <Button onClick={() => inputRef.current?.click()} disabled={Boolean(uploadProgress)}>
              <Upload className="h-4 w-4" />
              {uploadProgress ? `Uploading ${uploadProgress.done}/${uploadProgress.total}` : 'Upload .ini files'}
            </Button>
          </>
        )}
      </div>

      {loading ? (
        <div className="rounded-md border border-slate-200 bg-white p-10 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800">
          Loading NMS configurations…
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-300 bg-white p-10 text-center dark:border-slate-700 dark:bg-slate-800">
          <FileCode2 className="mx-auto mb-2 h-7 w-7 text-slate-300" />
          <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
            {rows.length ? 'No configurations match this search.' : 'No NMS configurations saved yet.'}
          </p>
          {projectId && canUpload(user) && !rows.length && (
            <p className="mt-1 text-xs text-slate-400">Upload one or more .ini files to start this vessel’s revision history.</p>
          )}
        </div>
      ) : (
        groups.map(([groupProjectId, groupRows]) => (
          <section key={groupProjectId || 'project'} className="overflow-hidden rounded-md border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800">
            {showProject && groupRows[0] && (
              <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-700">
                <div>
                  <h2 className="text-sm font-semibold text-slate-900 dark:text-white">{vesselLabel(groupRows[0])}</h2>
                  <p className="text-xs text-slate-400">{groupRows.length} current configuration{groupRows.length === 1 ? '' : 's'}</p>
                </div>
                <Link to={`/projects/${groupProjectId}?tab=nms`} className="text-xs font-medium text-blue-700 hover:underline dark:text-blue-300">
                  Open vessel NMS
                </Link>
              </div>
            )}
            {renderRows(groupRows)}
          </section>
        ))
      )}

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4" role="dialog" aria-modal="true">
          <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg border border-slate-300 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900">
            <div className="flex items-center gap-3 border-b border-slate-200 px-4 py-3 dark:border-slate-700">
              <FileCode2 className="h-5 w-5 text-blue-700" />
              <div className="min-w-0 flex-1">
                <h2 className="truncate text-sm font-semibold">{preview.row.original_filename}</h2>
                <p className="text-xs text-slate-400">Revision {preview.row.revision_number} · read-only preview</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => void download(preview.row)}>
                <Download className="h-4 w-4" /> Download
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setPreview(null)} aria-label="Close preview">
                <X className="h-4 w-4" />
              </Button>
            </div>
            <pre className="min-h-0 flex-1 overflow-auto whitespace-pre p-5 font-mono text-xs leading-5 text-slate-800 dark:text-slate-200">
              {preview.text}
            </pre>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        title="Delete NMS revision?"
        message={deleteTarget
          ? `Delete ${deleteTarget.original_filename} revision ${deleteTarget.revision_number}? If it is current, the previous revision becomes current.`
          : ''}
        confirmText="Delete revision"
        onConfirm={() => { if (deleteTarget) void remove(deleteTarget) }}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
}
